import { injectIntl } from "react-intl";
import React, { useEffect, useRef, useState } from "react";
import { RANGE_OPTIONS } from "./constants";
import AnalyticsDateRangePicker from "./AnalyticsDateRangePicker";

const TABS = ["Overview", "Engagement"];

const AnalyticsHeader = ({
  activeTab,
  onTabChange,
  activeRange,
  onRangeChange,
  lists,
  selectedList,
  onListChange,
  segmentOptions,
  selectedSegments,
  onSegmentToggle,
  dateRange,
  onDateRangeChange,
}) => {
  const [listOpen, setListOpen] = useState(false);
  const [segmentOpen, setSegmentOpen] = useState(false);
  const [segmentSearch, setSegmentSearch] = useState("");
  const listRef = useRef(null);
  const segmentRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) {
        setListOpen(false);
      }
      if (segmentRef.current && !segmentRef.current.contains(e.target)) {
        setSegmentOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allLists = [{ _id: "all", name: "All Lists" }, ...(lists || [])];

  const allSegmentOptions = [
    { name: "All Segments", value: "all" },
    ...(segmentOptions || []).map((name) => ({ name, value: name })),
  ];

  const filteredSegments = allSegmentOptions.filter((item) =>
    item.name.toLowerCase().includes(segmentSearch.toLowerCase())
  );

  const isAllSegmentsSelected =
    selectedSegments?.includes("all") || selectedSegments?.length === 0;

  const segmentButtonLabel = () => {
    if (isAllSegmentsSelected) return "All Segments";
    if (selectedSegments?.length === 1) {
      return selectedSegments[0] === "all"
        ? "All Segments"
        : selectedSegments[0];
    }
    return `${selectedSegments.length} Segments`;
  };

  const closeOtherDropdowns = (except) => {
    if (except !== "list") setListOpen(false);
    if (except !== "segment") setSegmentOpen(false);
  };

  return (
    <div className="SmsAnalyticsHeader">
      <div className="SmsAnalyticsHeaderLeft">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={
              activeTab === tab ? "SmsAnalyticsTabActive" : "SmsAnalyticsTab"
            }
            onClick={() => onTabChange(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="SmsAnalyticsHeaderRight">
        <div className="SmsAnalyticsHeaderRightLeft">Range:</div>
        <div className="SmsAnalyticsRangeSelector">
          {RANGE_OPTIONS.map((range) => (
            <button
              key={range}
              type="button"
              className={
                activeRange === range
                  ? "SmsAnalyticsRangeActive"
                  : "SmsAnalyticsRange"
              }
              onClick={() => onRangeChange(range)}
            >
              {range}
            </button>
          ))}
        </div>

        <div className="VerticalDivider"></div>

        <AnalyticsDateRangePicker
          value={dateRange}
          onChange={onDateRangeChange}
        />

        <div className="SmsAnalyticsDropdown" ref={listRef}>
          <button
            type="button"
            className={
              listOpen
                ? "SmsAnalyticsDropdownButton SmsAnalyticsDropdownButtonOpen"
                : "SmsAnalyticsDropdownButton"
            }
            onClick={() => {
              closeOtherDropdowns("list");
              setListOpen((prev) => !prev);
            }}
          >
            {selectedList?.name || "All Lists"}
            <img
              alt=""
              src={
                listOpen
                  ? "/media/icons/chevronUp.svg"
                  : "/media/icons/chevronDown.svg"
              }
              className="w-[14px] h-[14px]"
            />
          </button>
          {listOpen && (
            <div className="SmsAnalyticsDropdownMenu SmsAnalyticsListMenu">
              {allLists.map((item) => {
                const isSelected = selectedList?._id === item._id;
                return (
                  <button
                    key={item._id}
                    type="button"
                    className={
                      isSelected
                        ? "SmsAnalyticsDropdownItem SmsAnalyticsDropdownItemActive"
                        : "SmsAnalyticsDropdownItem"
                    }
                    onClick={() => {
                      onListChange(item);
                      setListOpen(false);
                    }}
                  >
                    {isSelected && (
                      <img
                        alt=""
                        src="/media/icons/check.svg"
                        className="SmsAnalyticsDropdownItemCheck"
                      />
                    )}
                    <span>{item.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="SmsAnalyticsDropdown" ref={segmentRef}>
          <button
            type="button"
            className={
              segmentOpen
                ? "SmsAnalyticsDropdownButton SmsAnalyticsDropdownButtonOpen"
                : "SmsAnalyticsDropdownButton"
            }
            onClick={() => {
              closeOtherDropdowns("segment");
              setSegmentOpen((prev) => !prev);
            }}
          >
            {segmentButtonLabel()}
            <img
              alt=""
              src={
                segmentOpen
                  ? "/media/icons/chevronUp.svg"
                  : "/media/icons/chevronDown.svg"
              }
              className="w-[14px] h-[14px]"
            />
          </button>
          {segmentOpen && (
            <div className="SmsAnalyticsDropdownMenu SmsAnalyticsSegmentMenu">
              <div className="SmsAnalyticsSegmentMenuHeader">
                FILTER BY SEGMENTS
              </div>
              <div className="SmsAnalyticsSegmentSearch">
                <img
                  alt=""
                  src="/media/icons/searchIcon.svg"
                  className="w-[16px] h-[16px]"
                />
                <input
                  type="text"
                  placeholder="Search"
                  value={segmentSearch}
                  onChange={(e) => setSegmentSearch(e.target.value)}
                />
              </div>
              <ul className="SmsAnalyticsSegmentList">
                {filteredSegments.map((item) => {
                  const isChecked =
                    item.value === "all"
                      ? isAllSegmentsSelected
                      : selectedSegments?.includes(item.value);
                  return (
                    <li
                      key={item.value}
                      className={`px-2 py-1.5 rounded ${
                        isChecked ? "bg-[#F6F6F9]" : "hover:bg-gray-50"
                      }`}
                    >
                      <label className="inline-flex w-full shrink-0 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isChecked}
                          onChange={() => onSegmentToggle(item.value)}
                          aria-label={`Select segment ${item.name}`}
                        />
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${
                            isChecked
                              ? "border-[#7F56D9] bg-[#F9F5FF]"
                              : "border-[#D0D5DD] bg-white"
                          }`}
                        >
                          {isChecked && (
                            <img
                              alt=""
                              src="/media/icons/check.svg"
                              className="w-3 h-3"
                            />
                          )}
                        </span>
                        <span
                          className="text-sm text-[#15151F] truncate"
                          style={{ fontFamily: "Inter" }}
                        >
                          {item.name}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <button type="button" className="SmsAnalyticsExportButton">
          <img alt="" src="/media/icons/download-01.svg" className="w-4 h-4" />
          Export
        </button>
      </div>
    </div>
  );
};

export default injectIntl(AnalyticsHeader);
